import React from "react";
import { MoreHorizontal20Regular } from "@fluentui/react-icons";
import { FocusRestoreContext, TreeActionsTabbableContext, isFocusLost } from "./SideMenuContexts";

export interface ActionMenuItem {
    label: React.ReactNode;
    onClick?: () => void;
    /** Shown greyed out. Stays focusable with the arrow keys (like Fluent's MenuItem), but does nothing. */
    disabled?: boolean;
}

export interface ActionMenuProps {
    /** Menu entries. With no items nothing is rendered. */
    items: ActionMenuItem[];
    /** Name of the "..." button. Include what it acts on, e.g. "More options for M12". */
    ariaLabel?: string;
}

interface Placement {
    top: number;
    left: number;
    above: boolean;
}

const VIEWPORT_MARGIN = 4;
const MENU_ITEM_SELECTOR = '[role="menuitem"]';

// Fluent's two-colour focus ring: the inner ring keeps it visible on a background in the focus colour (Teams high contrast).
const FOCUS_RING_CLASS = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--colorStrokeFocus2)] focus-visible:shadow-[inset_0_0_0_1px_var(--colorStrokeFocus1)]";

// The border is always there and only changes colour, so the button never changes size. Its colour comes from the
// tree row when there is one, which keeps it readable on the row's highlight.
const TRIGGER_CLASS = [
    "flex h-7 w-7 items-center justify-center rounded border border-[color:transparent] outline-none transition-colors duration-100 motion-reduce:transition-none [-webkit-tap-highlight-color:transparent]",
    "text-[color:var(--tree-row-fg,var(--colorNeutralForeground2))]",
    "[@media(hover:hover)]:hover:border-[color:var(--colorTransparentStroke)] [@media(hover:hover)]:hover:bg-[var(--colorSubtleBackgroundPressed)] [@media(hover:hover)]:hover:text-[color:var(--colorNeutralForeground2BrandHover)]",
    "aria-expanded:border-[color:var(--colorTransparentStroke)] aria-expanded:bg-[var(--colorSubtleBackgroundPressed)] aria-expanded:text-[color:var(--colorNeutralForeground2BrandPressed)]",
    "forced-colors:hover:outline forced-colors:hover:outline-1",
    FOCUS_RING_CLASS,
].join(" ");

const ITEM_CLASS = `flex min-h-8 w-full max-w-[290px] shrink-0 select-none items-start gap-1 rounded bg-[var(--colorNeutralBackground1)] p-1.5 text-left outline-none ${FOCUS_RING_CLASS}`;
const ENABLED_ITEM_CLASS = "cursor-pointer text-[color:var(--colorNeutralForeground2)] [@media(hover:hover)]:hover:bg-[var(--colorNeutralBackground1Hover)] [@media(hover:hover)]:hover:text-[color:var(--colorNeutralForeground2Hover)] active:bg-[var(--colorNeutralBackground1Pressed)] active:text-[color:var(--colorNeutralForeground2Pressed)] forced-colors:hover:outline forced-colors:hover:outline-1";
const DISABLED_ITEM_CLASS = "cursor-not-allowed text-[color:var(--colorNeutralForegroundDisabled)] forced-colors:text-[GrayText] forced-colors:[forced-color-adjust:none]";

/**
 * "More options" button with a popover menu of actions, styled like Fluent's subtle Button and Menu.
 * The popover is fixed positioned so the drawer's scroll container does not clip it, and it is rendered in place
 * (no portal) so it stays inside the drawer's modal dialog and its Fluent theme variables. That also means no
 * ancestor may have a transform, filter or will-change, which would make the popover position against it.
 * The popover opens below the button with their right edges lined up, so in a side drawer it stays over the drawer.
 * The wrapper carries `data-open` (TreeView keeps the actions slot visible while it is "true") and stops click
 * propagation, so using the menu inside a tree row never toggles the row.
 */
export function ActionMenu({ items, ariaLabel = "More options" }: ActionMenuProps) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [placement, setPlacement] = React.useState<Placement>();
    const tabbable = React.useContext(TreeActionsTabbableContext);
    const restoreFocus = React.useContext(FocusRestoreContext);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const focusLastOnOpen = React.useRef(false);
    const menuId = React.useId();
    const triggerId = `${menuId}-trigger`;

    const open = (focusLast: boolean) => {
        focusLastOnOpen.current = focusLast;
        setIsOpen(true);
    };

    const close = React.useCallback((restoreTriggerFocus: boolean) => {
        setIsOpen(false);
        setPlacement(undefined);
        if (restoreTriggerFocus) {
            triggerRef.current?.focus();
        }
    }, []);

    // Below the button and right-aligned with it, above it when it only fits there, kept inside the viewport.
    const place = React.useCallback(() => {
        const trigger = triggerRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) {
            return;
        }

        const anchor = trigger.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
        // Offset sizes, as the entry animation transforms the menu.
        const width = menu.offsetWidth;
        const height = menu.offsetHeight;

        const above = anchor.bottom + height > viewportHeight - VIEWPORT_MARGIN && anchor.top - height >= VIEWPORT_MARGIN;
        const top = above
            ? anchor.top - height
            : Math.max(VIEWPORT_MARGIN, Math.min(anchor.bottom, viewportHeight - VIEWPORT_MARGIN - height));
        const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.right - width, viewportWidth - VIEWPORT_MARGIN - width));

        setPlacement({ top, left, above });
    }, []);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        place();
        // Before paint the menu is still unplaced, but transparent rather than hidden, so it can take focus.
        const menuItems = menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR);
        const target = menuItems?.[focusLastOnOpen.current ? menuItems.length - 1 : 0];
        target?.focus({ preventScroll: true });
    }, [isOpen, place]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
                close(false);
            }
        };
        // Like a native context menu, scrolling anything but the menu closes it.
        const handleScroll = (event: Event) => {
            if (!menuRef.current?.contains(event.target as Node)) {
                close(false);
            }
        };
        // A resize can also mean the drawer around the trigger was hidden (SideDrawer fires one when it toggles).
        const handleResize = () => close(Boolean(menuRef.current?.contains(document.activeElement)));
        // A click outside the Teams frame never reaches this document, but it does blur the window.
        const handleBlur = () => close(false);

        document.addEventListener("mousedown", handleMouseDown);
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleResize);
        window.addEventListener("blur", handleBlur);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("blur", handleBlur);
        };
    }, [isOpen, close]);

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const menuItems = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? []);
        const index = menuItems.indexOf(document.activeElement as HTMLElement);
        const focusAt = (position: number) => menuItems[(position + menuItems.length) % menuItems.length]?.focus();

        switch (event.key) {
            case "Escape":
                close(true);
                break;
            case "Tab":
                // Back on the trigger first, so the browser moves focus on from there.
                close(true);
                return;
            case "ArrowDown":
                focusAt(index + 1);
                break;
            case "ArrowUp":
                focusAt(index < 0 ? -1 : index - 1);
                break;
            case "Home":
                focusAt(0);
                break;
            case "End":
                focusAt(-1);
                break;
            default: {
                // Type-ahead: focus the next item whose label starts with the typed character.
                if (event.key.length !== 1 || event.key === " " || event.ctrlKey || event.metaKey || event.altKey) {
                    return;
                }
                const character = event.key.toLowerCase();
                const candidates = menuItems.slice(index + 1).concat(menuItems.slice(0, Math.max(index, 0)));
                const match = candidates.find((item) => item.textContent?.trim().toLowerCase().startsWith(character));
                if (!match) {
                    return;
                }
                match.focus();
            }
        }

        event.preventDefault();
        event.stopPropagation();
    };

    const runItem = (item: ActionMenuItem) => {
        if (item.disabled) {
            return;
        }

        const trigger = triggerRef.current;
        close(true);
        item.onClick?.();
        if (restoreFocus) {
            // The action can remove this row (e.g. "Remove ..."), and the focused trigger with it.
            window.setTimeout(() => {
                if (trigger && !trigger.isConnected && isFocusLost()) {
                    restoreFocus();
                }
            });
        }
    };

    if (items.length === 0) {
        return null;
    }

    const menuStyle: React.CSSProperties = {
        boxShadow: "var(--shadow16)",
        maxHeight: `calc(100vh - ${2 * VIEWPORT_MARGIN}px)`,
        // Measured before it is placed.
        ...(placement ? { top: placement.top, left: placement.left } : { top: 0, left: 0, opacity: 0 }),
        ["--action-menu-slide" as string]: placement?.above ? "10px" : "-10px",
    };

    return (
        <div className="flex" data-open={isOpen} onClick={(event) => event.stopPropagation()}>
            <button
                ref={triggerRef}
                id={triggerId}
                type="button"
                tabIndex={tabbable ? undefined : -1}
                aria-label={ariaLabel}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                className={TRIGGER_CLASS}
                onClick={() => (isOpen ? close(false) : open(false))}
                onKeyDown={(event) => {
                    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !isOpen) {
                        event.preventDefault();
                        event.stopPropagation();
                        open(event.key === "ArrowUp");
                    } else if (event.key === "Escape" && isOpen) {
                        event.preventDefault();
                        event.stopPropagation();
                        close(true);
                    }
                }}
            >
                <MoreHorizontal20Regular />
            </button>

            {isOpen && (
                <div
                    ref={menuRef}
                    id={menuId}
                    role="menu"
                    aria-labelledby={triggerId}
                    style={menuStyle}
                    className="fixed z-50 flex w-max min-w-[138px] max-w-[300px] flex-col gap-0.5 overflow-y-auto overflow-x-hidden rounded border border-[color:var(--colorTransparentStroke)] bg-[var(--colorNeutralBackground1)] p-1 text-sm text-[color:var(--colorNeutralForeground1)] animate-action-menu-in motion-reduce:animate-none"
                    onKeyDown={handleMenuKeyDown}
                >
                    {items.map((item, index) => (
                        <button
                            key={index}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            aria-disabled={item.disabled || undefined}
                            className={`${ITEM_CLASS} ${item.disabled ? DISABLED_ITEM_CLASS : ENABLED_ITEM_CLASS}`}
                            // Like Fluent's menu, the item under the pointer is the focused one.
                            onMouseEnter={(event) => event.currentTarget.focus({ preventScroll: true })}
                            onClick={() => runItem(item)}
                        >
                            <span className="grow px-0.5">{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
